import { Route as AngularRoute } from '@angular/router';

import { EntityDetailComponent } from '@connect-services/client/components/entity-detail/entity-detail.component';
import { FieldInterventionEntitiesFilteredTableComponent } from '@connect-services/client/components/field-intervention-entities-filtered-table/field-intervention-entities-filtered-table.component';
import { FieldInterventionEntityDetailsComponent } from '@connect-services/client/components/field-intervention-entity-details/field-intervention-entity-details.component';
import { FieldInterventionLayerDetailComponent } from '@connect-services/client/components/field-intervention-layer-detail/field-intervention-layer-detail.component';
import { FieldInterventionLayersFilteredTableComponent } from '@connect-services/client/components/field-intervention-layers-filtered-table/field-intervention-layers-filtered-table.component';
import { FieldInterventionListComponent } from '@connect-services/client/components/field-intervention-list/field-intervention-list.component';
import { DepositDetailComponent as DepositDetailSettingsComponent } from '@connect-services/client/components/project-settings/deposit-detail/deposit-detail.component';
import { ProjectSettingsLayerDetailResolverComponent } from '@connect-services/client/components/project-settings/project-settings-layer-detail-resolver/project-settings-layer-detail-resolver.component';
import { SourcesComponent } from '@connect-services/client/components/sources/sources.component';
import { StatisticsComponent } from '@connect-services/client/components/statistics/statistics.component';
import { SynopticComponent } from '@connect-services/client/components/synoptic/synoptic.component';
import { TableEntityDetailComponent } from '@connect-services/client/components/table-entity-detail/table-entity-detail.component';
import { authenticatedGuard } from '@connect-services/client/guards/authenticated.guard';
import { canDeactivateGuard } from '@connect-services/client/guards/can-deactivate.guard';
import { canDismountGuard } from '@connect-services/client/guards/can-dismount.guard';
import { CanNavigateGuard } from '@connect-services/client/guards/can-navigate.guard';
import { isDoticAdminGuard } from '@connect-services/client/guards/is-dotic-admin.guard';
import { LeaveEditModeOrSelectionGuard } from '@connect-services/client/guards/leave-edit-mode-or-selection-guard.guard';
import { notAuthenticatedGuard } from '@connect-services/client/guards/not-authenticated.guard';
import { ReloadGuard } from '@connect-services/client/guards/reload.guard';
import {
    ManagementDataAccessContextEnum,
    WarehouseAndFormManagementDataAccessGuard,
} from '@connect-services/client/guards/warehouse-and-form-management-data-access.guard';
import { RouteNameEnum, RoutePathEnum } from '@connect-services/client/models/routes.model';
import { AiComponent } from '@connect-services/client/pages/ai/ai.component';
import { EntityDetailCreateEditComponent } from '@connect-services/client/pages/entity-detail/create-edit/entity-detail-create-edit.component';
import { ExportStatisticsComponent } from '@connect-services/client/pages/export-statistics/export-statistics.component';
import { CreateFieldInterventionComponent } from '@connect-services/client/pages/field-intervention/create-field-intervention/create-field-intervention.component';
import { FieldInterventionDashboardComponent } from '@connect-services/client/pages/field-intervention/field-intervention-dashboard/field-intervention-dashboard.component';
import { FieldInterventionEntityPlanningComponent } from '@connect-services/client/pages/field-intervention/field-intervention-entity-planning/field-intervention-entity-planning.component';
import { FieldInterventionFormAnswerComponent } from '@connect-services/client/pages/field-intervention/field-intervention-form-answer/field-intervention-form-answer.component';
import { CreateEditLayerFilterComponent } from '@connect-services/client/pages/filter/layer/create-layer-filter/create-edit-layer-filter.component';
import { HomeComponent } from '@connect-services/client/pages/home/home.component';
import {
    DataViewType,
    DepositComponent,
} from '@connect-services/client/pages/management/data/deposit/deposit.component';
import { DepositDetailComponent } from '@connect-services/client/pages/management/data/deposit/deposit-detail/deposit-detail.component';
import { DepositEditionComponent } from '@connect-services/client/pages/management/data/deposit/deposit-edition/deposit-edition.component';
import { FormDetailComponent } from '@connect-services/client/pages/management/data/forms/form-detail/form-detail.component';
import { FormsComponent } from '@connect-services/client/pages/management/data/forms/forms.component';
import { DetailPanelComponent } from '@connect-services/client/pages/management/detail-panel/detail-panel.component';
import { ManagementComponent } from '@connect-services/client/pages/management/management.component';
import { ManagementNoPermissionComponent } from '@connect-services/client/pages/management/no-permission/management-no-permission.component';
import { NotificationComponent } from '@connect-services/client/pages/management/organization/notification/notification.component';
import { PasswordRulesComponent } from '@connect-services/client/pages/management/organization/password-rules/password-rules.component';
import { RoleDetailComponent } from '@connect-services/client/pages/management/organization/role-detail/role-detail.component';
import { RoleEditionComponent } from '@connect-services/client/pages/management/organization/role-edition/role-edition.component';
import { UserDetailComponent } from '@connect-services/client/pages/management/organization/user-detail/user-detail.component';
import { UserEditComponent } from '@connect-services/client/pages/management/organization/user-edit/user-edit.component';
import { UsersRolesComponent } from '@connect-services/client/pages/management/organization/users-roles/users-roles.component';
import { ProjectRedirectionComponentV2 } from '@connect-services/client/pages/project-redirection/project-redirection.component';
import { ProjectSettingsComponent } from '@connect-services/client/pages/project-settings/project-settings.component';
import { ChangePasswordComponent } from '@connect-services/client/pages/security/change-password/change-password.component';
import { FinalizeInvitationComponent } from '@connect-services/client/pages/security/finalize-invitation/finalize-invitation.component';
import { ForgotPasswordComponent } from '@connect-services/client/pages/security/forgot-password/forgot-password.component';
import { LoginComponent } from '@connect-services/client/pages/security/login/login.component';
import { SecurityComponent } from '@connect-services/client/pages/security/security.component';
import { SsoLoginComponent } from '@connect-services/client/pages/security/sso-login/sso-login.component';
import { blocResolver } from '@connect-services/client/resolvers/bloc.resolver';
import { featureSessionResolver } from '@connect-services/client/resolvers/feature-session.resolver';
import { filterResolver } from '@connect-services/client/resolvers/filter.resolver';
import { gridResolver } from '@connect-services/client/resolvers/grid.resolver';
import { SessionResolver } from '@connect-services/client/resolvers/session.resolver';
import { FeatureSession } from '@connect-services/src/sdk/api';

export type RouteData = {
    featureSession?: FeatureSession.FeatureTrackedEnum | RouteNameEnum.HOME;
    managementAccess?: ManagementDataAccessContextEnum;
    name?: string;
    redirectTo?: string;
    viewType?: DataViewType;
};

export type Route = Omit<AngularRoute, 'data' | 'children'> & {
    children?: Routes;
    data?: RouteData;
};

export type Routes = Route[];

export const routes: Routes = [
    /* eslint-disable sort-keys */
    {
        path: '',
        canActivateChild: [authenticatedGuard, CanNavigateGuard],
        resolve: { session: SessionResolver },
        children: [
            {
                path: '',
                pathMatch: 'full',
                component: ProjectRedirectionComponentV2,
            },
            {
                path: RoutePathEnum.PROJECTS,
                data: {
                    name: RoutePathEnum.PROJECTS,
                },
                children: [
                    { path: '', component: ProjectRedirectionComponentV2 },
                    {
                        path: RoutePathEnum.HOME,
                        component: HomeComponent,
                        resolve: {
                            bloc: blocResolver,
                            filter: filterResolver,
                            grid: gridResolver,
                        },
                        data: { name: RoutePathEnum.HOME },
                        runGuardsAndResolvers: 'always',
                    },
                    {
                        path: `:${RoutePathEnum.PROJECT_ID}`,
                        component: HomeComponent,
                        resolve: {
                            bloc: blocResolver,
                            featureSession: featureSessionResolver,
                            grid: gridResolver,
                            filter: filterResolver,
                        },
                        data: {
                            featureSession: RouteNameEnum.HOME,
                            name: RoutePathEnum.PROJECT_ID,
                        },
                        runGuardsAndResolvers: 'always',
                        children: [
                            {
                                path: RoutePathEnum.LAYER_FILTER,
                                children: [
                                    {
                                        path: '',
                                        component: CreateEditLayerFilterComponent,
                                        resolve: { bloc: blocResolver, grid: gridResolver },
                                        data: { name: RoutePathEnum.LAYER_FILTER },
                                        runGuardsAndResolvers: 'always',
                                    },
                                    {
                                        path: `:${RoutePathEnum.LAYER_FILTER_ID}`,
                                        component: CreateEditLayerFilterComponent,
                                        resolve: { bloc: blocResolver, grid: gridResolver },
                                        data: { name: RoutePathEnum.LAYER_FILTER_ID },
                                        runGuardsAndResolvers: 'always',
                                    },
                                ],
                            },
                            {
                                path: RoutePathEnum.TABLE_ENTITY_DETAIL,
                                data: {
                                    name: RoutePathEnum.TABLE_ENTITY_DETAIL,
                                },
                                children: [
                                    {
                                        path: `:${RoutePathEnum.TABLE_ENTITY_ID}`,
                                        component: TableEntityDetailComponent,
                                        resolve: { bloc: blocResolver, grid: gridResolver },
                                        data: { name: RoutePathEnum.TABLE_ENTITY_ID },
                                        runGuardsAndResolvers: 'always',
                                    },
                                ],
                            },
                            {
                                path: `${RoutePathEnum.LAYERS}`,
                                resolve: { bloc: blocResolver, featureSession: featureSessionResolver },
                                data: { name: RoutePathEnum.LAYERS },
                                children: [
                                    {
                                        path: '',
                                        resolve: { bloc: blocResolver, grid: gridResolver },
                                        component: SourcesComponent,
                                        data: { name: RoutePathEnum.LAYERS },
                                    },
                                    {
                                        path: `:${RoutePathEnum.LAYER_ID}`,
                                        children: [
                                            {
                                                path: RoutePathEnum.ENTITY_DETAIL,
                                                children: [
                                                    {
                                                        path: `:${RoutePathEnum.ENTITY_ID}`,
                                                        children: [
                                                            {
                                                                canActivate: [ReloadGuard],
                                                                path: ``,
                                                                component: EntityDetailComponent,
                                                                resolve: { bloc: blocResolver, grid: gridResolver },
                                                                data: { name: RoutePathEnum.ENTITY_DETAIL },
                                                                runGuardsAndResolvers: 'always',
                                                            },
                                                            {
                                                                canActivate: [ReloadGuard],
                                                                canDeactivate: [canDeactivateGuard],
                                                                path: `CREATE`,
                                                                component: EntityDetailCreateEditComponent,
                                                                resolve: { bloc: blocResolver, grid: gridResolver },
                                                                data: { name: RoutePathEnum.ENTITY_DETAIL },
                                                                runGuardsAndResolvers: 'always',
                                                            },
                                                        ],
                                                    },
                                                ],
                                            },
                                            {
                                                path: RoutePathEnum.NETWORK_PATH,
                                                children: [
                                                    {
                                                        path: `:${RoutePathEnum.ENTITY_ID}`,
                                                        loadComponent: () =>
                                                            import('@connect-services/client/components/network-path/network-path.component').then(
                                                                (component) => component.NetworkPathComponent,
                                                            ),
                                                        resolve: {
                                                            grid: gridResolver,
                                                        },
                                                        data: { name: RoutePathEnum.NETWORK_PATH },
                                                        runGuardsAndResolvers: 'always',
                                                    },
                                                ],
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                canActivate: [LeaveEditModeOrSelectionGuard],
                                canDeactivate: [canDeactivateGuard],
                                path: RoutePathEnum.STATISTICS,
                                component: StatisticsComponent,
                                resolve: { grid: gridResolver, featureSession: featureSessionResolver },
                                data: {
                                    featureSession: FeatureSession.FeatureTrackedEnum.STATISTIC,
                                    name: RouteNameEnum.STATISTICS,
                                },
                            },
                            {
                                canActivate: [LeaveEditModeOrSelectionGuard],
                                path: RoutePathEnum.FIELD_INTERVENTIONS,
                                resolve: { bloc: blocResolver, featureSession: featureSessionResolver },
                                runGuardsAndResolvers: 'always',
                                data: {
                                    featureSession: FeatureSession.FeatureTrackedEnum.FIELD_INTERVENTION,
                                    name: RoutePathEnum.FIELD_INTERVENTIONS,
                                },
                                children: [
                                    {
                                        path: '',
                                        resolve: { grid: gridResolver },
                                        component: FieldInterventionListComponent,
                                        data: { name: RoutePathEnum.FIELD_INTERVENTIONS },
                                        runGuardsAndResolvers: 'always',
                                    },
                                    {
                                        path: `:${RoutePathEnum.FIELD_INTERVENTION_ID}`,
                                        resolve: { bloc: blocResolver },
                                        runGuardsAndResolvers: 'always',
                                        data: { name: RoutePathEnum.FIELD_INTERVENTION_ID },
                                        children: [
                                            {
                                                path: RoutePathEnum.FIELD_INTERVENTION_CHANGE_SELECTION,
                                                resolve: { bloc: blocResolver, grid: gridResolver },
                                                component: CreateFieldInterventionComponent,
                                                data: { name: RoutePathEnum.FIELD_INTERVENTION_CREATE },
                                            },
                                            {
                                                path: RoutePathEnum.FIELD_INTERVENTION_CREATE,
                                                resolve: { bloc: blocResolver, grid: gridResolver },
                                                component: CreateFieldInterventionComponent,
                                                data: { name: RoutePathEnum.FIELD_INTERVENTION_CREATE },
                                            },
                                            {
                                                path: RoutePathEnum.FIELD_INTERVENTION_DASHBOARD,
                                                resolve: {
                                                    bloc: blocResolver,
                                                    grid: gridResolver,
                                                },
                                                component: FieldInterventionDashboardComponent,
                                                data: { name: RoutePathEnum.FIELD_INTERVENTION_DASHBOARD },
                                                runGuardsAndResolvers: 'always',
                                            },
                                            {
                                                path: RoutePathEnum.FIELD_INTERVENTION_PLANNING,
                                                resolve: { grid: gridResolver },
                                                data: { name: RoutePathEnum.FIELD_INTERVENTION_PLANNING },
                                                runGuardsAndResolvers: 'always',
                                                children: [
                                                    {
                                                        path: '',
                                                        resolve: {
                                                            bloc: blocResolver,
                                                            filter: filterResolver,
                                                            grid: gridResolver,
                                                        },
                                                        component: FieldInterventionEntityPlanningComponent,
                                                        data: { name: RoutePathEnum.FIELD_INTERVENTION_ID },
                                                        runGuardsAndResolvers: 'always',
                                                    },
                                                    {
                                                        path: `:${RoutePathEnum.FIELD_INTERVENTION_ENTITY_ID}`,
                                                        component: FieldInterventionEntityDetailsComponent,
                                                        outlet: 'detailPanel',
                                                        resolve: {
                                                            bloc: blocResolver,
                                                            grid: gridResolver,
                                                        },
                                                        data: { name: RoutePathEnum.FIELD_INTERVENTION_ENTITY_ID },
                                                        runGuardsAndResolvers: 'always',
                                                    },
                                                ],
                                            },
                                            {
                                                path: RoutePathEnum.FIELD_INTERVENTION_ENTITIES,
                                                resolve: {
                                                    bloc: blocResolver,
                                                    filter: filterResolver,
                                                    grid: gridResolver,
                                                },
                                                runGuardsAndResolvers: 'always',
                                                data: { name: RoutePathEnum.FIELD_INTERVENTION_ENTITIES },
                                                children: [
                                                    {
                                                        path: '',
                                                        canDeactivate: [canDismountGuard],
                                                        component: FieldInterventionEntitiesFilteredTableComponent,
                                                        resolve: {
                                                            bloc: blocResolver,
                                                            grid: gridResolver,
                                                        },
                                                        data: { name: RoutePathEnum.FIELD_INTERVENTION_ENTITIES },
                                                        runGuardsAndResolvers: 'always',
                                                    },
                                                    {
                                                        path: `:${RoutePathEnum.FIELD_INTERVENTION_ENTITY_ID}`,
                                                        component: FieldInterventionEntityDetailsComponent,
                                                        outlet: 'detailPanel',
                                                        resolve: { bloc: blocResolver, grid: gridResolver },
                                                        data: { name: RoutePathEnum.FIELD_INTERVENTION_ENTITY_ID },
                                                        runGuardsAndResolvers: 'always',
                                                    },
                                                ],
                                            },
                                            {
                                                path: RoutePathEnum.FIELD_INTERVENTION_FORMS,
                                                resolve: { grid: gridResolver },
                                                data: { name: RoutePathEnum.FIELD_INTERVENTION_FORMS },
                                                children: [
                                                    {
                                                        path: '',
                                                        component: FieldInterventionFormAnswerComponent,
                                                        resolve: { bloc: blocResolver, grid: gridResolver },
                                                        data: { name: RoutePathEnum.FIELD_INTERVENTION_FORMS },
                                                        runGuardsAndResolvers: 'always',
                                                    },
                                                    {
                                                        path: `:${RoutePathEnum.FIELD_INTERVENTION_ENTITY_ID}`,
                                                        component: FieldInterventionEntityDetailsComponent,
                                                        outlet: 'detailPanel',
                                                        resolve: {
                                                            bloc: blocResolver,
                                                            grid: gridResolver,
                                                        },
                                                        data: { name: RoutePathEnum.FIELD_INTERVENTION_ENTITY_ID },
                                                        runGuardsAndResolvers: 'always',
                                                    },
                                                ],
                                            },
                                            {
                                                path: RoutePathEnum.FIELD_INTERVENTION_LAYERS,
                                                resolve: { bloc: blocResolver, grid: gridResolver },
                                                runGuardsAndResolvers: 'always',
                                                data: { name: RoutePathEnum.FIELD_INTERVENTION_LAYERS },
                                                children: [
                                                    {
                                                        path: '',
                                                        component: FieldInterventionLayersFilteredTableComponent,
                                                        resolve: { bloc: blocResolver, grid: gridResolver },
                                                        data: { name: RoutePathEnum.FIELD_INTERVENTION_LAYERS },
                                                        runGuardsAndResolvers: 'always',
                                                    },
                                                    {
                                                        path: `:${RoutePathEnum.FIELD_INTERVENTION_LAYER_ID}`,
                                                        component: FieldInterventionLayerDetailComponent,
                                                        outlet: 'detailPanel',
                                                        resolve: { bloc: blocResolver, grid: gridResolver },
                                                        data: { name: RoutePathEnum.FIELD_INTERVENTION_LAYER_ID },
                                                        runGuardsAndResolvers: 'always',
                                                    },
                                                ],
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: RoutePathEnum.SYNOPTIC,
                                data: {
                                    featureSession: FeatureSession.FeatureTrackedEnum.SYNOPTIC,
                                    name: RoutePathEnum.SYNOPTIC,
                                },
                                resolve: { featureSession: featureSessionResolver },
                                children: [
                                    ...[
                                        '',
                                        RoutePathEnum.EXT,
                                        [RoutePathEnum.EXT, RoutePathEnum.SYNOPTIC_PLAN].join('/'),
                                    ].map((path: string, index: number) => ({
                                        path,
                                        component: SynopticComponent,
                                        resolve: {
                                            bloc: blocResolver,
                                            grid: gridResolver,
                                        },
                                        data: {
                                            name:
                                                index === 0
                                                    ? RoutePathEnum.SYNOPTIC
                                                    : index === 1
                                                      ? RoutePathEnum.EXT
                                                      : RoutePathEnum.SYNOPTIC_PLAN,
                                        },
                                    })),
                                ],
                            },
                            {
                                path: RoutePathEnum.SETTINGS,
                                resolve: { featureSession: featureSessionResolver },
                                data: { featureSession: FeatureSession.FeatureTrackedEnum.SETTINGS },
                                children: [
                                    {
                                        path: '',
                                        component: ProjectSettingsComponent,
                                        resolve: { bloc: blocResolver, grid: gridResolver },
                                        data: { name: RoutePathEnum.SETTINGS },
                                        runGuardsAndResolvers: 'always',
                                    },
                                    {
                                        path: `:${RoutePathEnum.DETAIL_LAYER_ID}`,
                                        component: ProjectSettingsLayerDetailResolverComponent,
                                        outlet: 'detailPanel',
                                        resolve: { bloc: blocResolver, grid: gridResolver },
                                        data: { name: RoutePathEnum.DETAIL_LAYER_ID },
                                        runGuardsAndResolvers: 'always',
                                    },
                                    {
                                        path: RoutePathEnum.DEPOSIT_DETAIL,
                                        outlet: 'detailPanel',
                                        children: [
                                            {
                                                path: `:${RoutePathEnum.DEPOSIT_DETAIL_ID}`,
                                                component: DepositDetailSettingsComponent,
                                                data: { name: RoutePathEnum.DEPOSIT_DETAIL_ID },
                                                resolve: { bloc: blocResolver, grid: gridResolver },
                                                runGuardsAndResolvers: 'always',
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: RoutePathEnum.AI,
                                children: [
                                    {
                                        path: '',
                                        component: AiComponent,
                                        resolve: {
                                            bloc: blocResolver,
                                            featureSession: featureSessionResolver,
                                            grid: gridResolver,
                                        },
                                        data: {
                                            featureSession: FeatureSession.FeatureTrackedEnum.CHATBOT,
                                            name: RouteNameEnum.AI,
                                        },
                                        runGuardsAndResolvers: 'always',
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                path: RoutePathEnum.MANAGEMENT_ORGANIZATION,
                component: ManagementComponent,
                resolve: { featureSession: featureSessionResolver },
                children: [
                    {
                        // This child is used to redirect the user on the first tab if it has not specified one
                        // So you can change the "redirectTo" value if you want to redirect to another page/tab/panel
                        path: '',
                        pathMatch: 'full',
                        redirectTo: RoutePathEnum.USERS_ROLES,
                    },
                    {
                        // And this one is a simple example of how to implement a tab (in the management panel)
                        // with a detail panel outlet that toggles on some event (uncomment children to test)
                        path: RoutePathEnum.USERS_ROLES,
                        resolve: { bloc: blocResolver },
                        children: [
                            // This first child is very important if you want the detail panel to work.
                            // You must have at least 2 children
                            {
                                path: '',
                                component: UsersRolesComponent,
                                resolve: { bloc: blocResolver },
                                data: { name: RouteNameEnum.USERS_ROLES },
                            },
                            {
                                path: RoutePathEnum.USER,
                                component: DetailPanelComponent,
                                outlet: 'detailPanel', // this is where you specify your panel outlet name
                                children: [
                                    {
                                        path: `:${RoutePathEnum.USER_ID}`,
                                        resolve: { bloc: blocResolver },
                                        children: [
                                            {
                                                path: '',
                                                resolve: { bloc: blocResolver },
                                                component: UserDetailComponent,
                                                data: { name: RoutePathEnum.USER_ID },
                                                runGuardsAndResolvers: 'always',
                                            },
                                            {
                                                path: RoutePathEnum.USER_EDITION,
                                                resolve: { bloc: blocResolver },
                                                component: UserEditComponent,
                                                data: { name: RoutePathEnum.USER_EDITION },
                                                runGuardsAndResolvers: 'always',
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: RoutePathEnum.ROLE,
                                component: DetailPanelComponent,
                                resolve: { bloc: blocResolver },
                                outlet: 'detailPanel',
                                children: [
                                    {
                                        path: `:${RoutePathEnum.ROLE_ID}`,
                                        resolve: { bloc: blocResolver },
                                        children: [
                                            {
                                                path: '',
                                                resolve: { bloc: blocResolver },
                                                component: RoleDetailComponent,
                                                data: { name: RoutePathEnum.ROLE_ID },
                                                runGuardsAndResolvers: 'always',
                                            },
                                            {
                                                path: RoutePathEnum.ROLE_EDITION,
                                                resolve: { bloc: blocResolver },
                                                component: RoleEditionComponent,
                                                data: { name: RoutePathEnum.ROLE_EDITION },
                                                runGuardsAndResolvers: 'always',
                                            },
                                        ],
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        path: RoutePathEnum.NOTIFICATION,
                        canActivate: [isDoticAdminGuard],
                        resolve: { bloc: blocResolver },
                        component: NotificationComponent,
                        data: { name: RouteNameEnum.NOTIFICATION },
                    },
                    {
                        path: RoutePathEnum.PASSWORD_RULES,
                        canActivate: [isDoticAdminGuard],
                        component: PasswordRulesComponent,
                        data: { name: RouteNameEnum.PASSWORD_RULES },
                    },
                    {
                        path: RoutePathEnum.MANAGEMENT_NO_PERMISSION,
                        component: ManagementNoPermissionComponent,
                    },
                ],
            },
            {
                path: RoutePathEnum.MANAGEMENT_DATA,
                component: ManagementComponent,
                resolve: { featureSession: featureSessionResolver },
                children: [
                    {
                        // This child is used to redirect the user on the first tab if it has not specified one
                        // So you can change the "redirectTo" value if you want to redirect to another page/tab/panel
                        path: '',
                        pathMatch: 'full',
                        canActivate: [WarehouseAndFormManagementDataAccessGuard],
                        data: { managementAccess: ManagementDataAccessContextEnum.LANDING },
                        component: ManagementNoPermissionComponent,
                    },
                    {
                        path: RoutePathEnum.FORMS,
                        canActivate: [WarehouseAndFormManagementDataAccessGuard],
                        data: { managementAccess: ManagementDataAccessContextEnum.FORMS },
                        resolve: { bloc: blocResolver },
                        children: [
                            {
                                path: '',
                                component: FormsComponent,
                                data: { name: RouteNameEnum.FORMS },
                            },
                            {
                                path: `:${RoutePathEnum.FIELD_INTERVENTION_FORM_ID}`,
                                component: FormDetailComponent,
                                data: { name: RoutePathEnum.FIELD_INTERVENTION_FORM_ID },
                                resolve: { bloc: blocResolver },
                            },
                        ],
                    },
                    {
                        path: RoutePathEnum.ORGANIZATION_DATA,
                        canActivate: [WarehouseAndFormManagementDataAccessGuard],
                        data: {
                            managementAccess: ManagementDataAccessContextEnum.WAREHOUSE,
                        },
                        resolve: { bloc: blocResolver },
                        children: [
                            {
                                path: '',
                                component: DepositComponent,
                                data: {
                                    name: RouteNameEnum.ORGANIZATION_DATA,
                                    viewType: DataViewType.ORGANIZATION_DATA,
                                },
                            },
                            {
                                path: `:${RoutePathEnum.DEPOSIT_ID}`,
                                component: DetailPanelComponent,
                                resolve: { bloc: blocResolver },
                                outlet: 'detailPanel',
                                children: [
                                    {
                                        path: '',
                                        resolve: { bloc: blocResolver },
                                        component: DepositDetailComponent,
                                        data: { name: RoutePathEnum.DEPOSIT_ID },
                                        runGuardsAndResolvers: 'always',
                                    },
                                    {
                                        path: RoutePathEnum.DEPOSIT_EDITION,
                                        resolve: { bloc: blocResolver },
                                        component: DepositEditionComponent,
                                        data: { name: RoutePathEnum.DEPOSIT_EDITION },
                                        runGuardsAndResolvers: 'always',
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        path: RoutePathEnum.REFERENCE_DATA,
                        canActivate: [WarehouseAndFormManagementDataAccessGuard],
                        data: {
                            managementAccess: ManagementDataAccessContextEnum.WAREHOUSE,
                            redirectTo: RoutePathEnum.ORGANIZATION_DATA,
                        },
                        resolve: { bloc: blocResolver },
                        children: [
                            {
                                path: '',
                                component: DepositComponent,
                                data: {
                                    name: RouteNameEnum.REFERENCE_DATA,
                                    viewType: DataViewType.REFERENCE_DATA,
                                },
                            },
                            {
                                path: `:${RoutePathEnum.DEPOSIT_ID}`,
                                component: DetailPanelComponent,
                                resolve: { bloc: blocResolver },
                                outlet: 'detailPanel',
                                children: [
                                    {
                                        path: '',
                                        resolve: { bloc: blocResolver },
                                        component: DepositDetailComponent,
                                        data: { name: RoutePathEnum.DEPOSIT_ID },
                                        runGuardsAndResolvers: 'always',
                                    },
                                    {
                                        path: RoutePathEnum.DEPOSIT_EDITION,
                                        resolve: { bloc: blocResolver },
                                        component: DepositEditionComponent,
                                        data: { name: RoutePathEnum.DEPOSIT_EDITION },
                                        runGuardsAndResolvers: 'always',
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        path: RoutePathEnum.MANAGEMENT_NO_PERMISSION,
                        component: ManagementNoPermissionComponent,
                    },
                ],
            },
        ],
    },
    {
        path: 'statistics-export',
        canActivateChild: [authenticatedGuard],
        component: ExportStatisticsComponent,
    },
    {
        canActivate: [notAuthenticatedGuard],
        path: RoutePathEnum.ONE_GEO_SUITE,
        children: [
            {
                path: RoutePathEnum.LOGIN,
                data: {
                    name: RouteNameEnum.LOGIN,
                },
                loadComponent: () =>
                    import('./pages/one-geo-suite/ogs-login/ogs-login.component').then(
                        (component) => component.OgsLoginComponent,
                    ),
            },
        ],
    },
    {
        path: '',
        component: SecurityComponent,
        children: [
            {
                canActivate: [notAuthenticatedGuard],
                path: RoutePathEnum.CHANGE_PASSWORD,
                component: ChangePasswordComponent,
            },
            {
                path: RoutePathEnum.FINALIZE_INVITATION,
                component: FinalizeInvitationComponent,
            },
            {
                canActivate: [notAuthenticatedGuard],
                path: RoutePathEnum.FORGOT_PASSWORD,
                component: ForgotPasswordComponent,
            },
            {
                canActivate: [notAuthenticatedGuard],
                path: `${RoutePathEnum.SSO}/${RoutePathEnum.LOGIN}`,
                component: SsoLoginComponent,
            },
            {
                canActivate: [notAuthenticatedGuard],
                path: RoutePathEnum.LOGIN,
                data: {
                    name: RouteNameEnum.LOGIN,
                },
                component: LoginComponent,
            },
        ],
    },
    /* eslint-enable sort-keys */
];
